/**
 * Local hotel-name filter for customer vacation chat (no extra search API).
 * Primary hotel list: server `state.last_results_summary` (full snapshot).
 */

/** @param {unknown} s */
export function normalizeCvacHotelLabel(s) {
  if (s == null) return "";
  const t = typeof s === "string" ? s : String(s);
  return t.replace(/\s+/g, " ").trim();
}

/**
 * @param {unknown} a
 * @param {unknown} b
 */
export function cvacHotelNamesMatch(a, b) {
  const x = normalizeCvacHotelLabel(a).toLowerCase();
  const y = normalizeCvacHotelLabel(b).toLowerCase();
  if (!x || !y) return false;
  return x === y;
}

/**
 * @param {unknown} state
 * @returns {string[]}
 */
export function extractUniqueHotelNamesFromLastResultsSummary(state) {
  const rows = state?.last_results_summary;
  if (!Array.isArray(rows)) return [];
  /** @type {Map<string, string>} */
  const seen = new Map();
  for (const row of rows) {
    const raw = row?.hotel_name ?? row?.title ?? "";
    const disp = normalizeCvacHotelLabel(raw);
    if (!disp) continue;
    const key = disp.toLowerCase();
    if (!seen.has(key)) seen.set(key, disp);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, "he"));
}

/**
 * Fallback when session state is missing (e.g. history reload): union from loaded cards only.
 * @param {unknown[]} messages
 * @returns {string[]}
 */
export function extractUniqueHotelNamesFromMessageCards(messages) {
  if (!Array.isArray(messages)) return [];
  /** @type {Map<string, string>} */
  const seen = new Map();
  for (const m of messages) {
    if (!m || m.role !== "assistant" || !Array.isArray(m.cards)) continue;
    for (const c of m.cards) {
      const raw = c?.hotel_name ?? c?.title ?? "";
      const disp = normalizeCvacHotelLabel(raw);
      if (!disp) continue;
      const key = disp.toLowerCase();
      if (!seen.has(key)) seen.set(key, disp);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, "he"));
}

/**
 * @param {unknown} serverState
 * @param {unknown[]} messages
 * @returns {string[]}
 */
export function resolveAvailableHotelFilters(serverState, messages) {
  const fromState = extractUniqueHotelNamesFromLastResultsSummary(serverState);
  if (fromState.length) return fromState;
  return extractUniqueHotelNamesFromMessageCards(messages);
}

/**
 * Stable fingerprint for the current search snapshot (load-more keeps the same summary).
 * @param {unknown} state
 */
export function fingerprintLastResultsSummary(state) {
  const rows = state?.last_results_summary;
  if (!Array.isArray(rows) || rows.length === 0) return "";
  return rows
    .map((r) => `${r?.offer_rank ?? ""}:${String(r?.offer_id ?? "")}`)
    .join("|");
}

/**
 * @param {unknown[]} cards
 * @param {string|null} selectedName
 */
export function filterCardsByHotelName(cards, selectedName) {
  if (!selectedName) return Array.isArray(cards) ? cards : [];
  const list = Array.isArray(cards) ? cards : [];
  return list.filter((c) => cvacHotelNamesMatch(c?.hotel_name ?? c?.title, selectedName));
}

/**
 * @param {unknown} state
 * @param {string|null} selectedName
 */
export function countSummaryRowsForHotel(state, selectedName) {
  if (!selectedName) return 0;
  const rows = state?.last_results_summary;
  if (!Array.isArray(rows)) return 0;
  return rows.filter((r) => cvacHotelNamesMatch(r?.hotel_name ?? r?.title, selectedName)).length;
}

/**
 * Full snapshot rows for a hotel (same matching rules as {@link countSummaryRowsForHotel}).
 * @param {unknown} state
 * @param {string|null} selectedName
 * @returns {unknown[]}
 */
export function filterSummaryRowsForHotel(state, selectedName) {
  if (!selectedName) return [];
  const rows = state?.last_results_summary;
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => cvacHotelNamesMatch(r?.hotel_name ?? r?.title, selectedName));
}

/**
 * @param {unknown[]} messages
 * @returns {unknown[]}
 */
export function collectAllLoadedAssistantCards(messages) {
  if (!Array.isArray(messages)) return [];
  const out = [];
  for (const m of messages) {
    if (!m || m.role !== "assistant" || !Array.isArray(m.cards)) continue;
    for (const c of m.cards) {
      if (c && typeof c === "object") out.push(c);
    }
  }
  return out;
}

/**
 * Minimal card shape for {@link HotelShortlistCard} from a `last_results_summary` row.
 * @param {unknown} row
 */
export function summaryRowToChatCard(row) {
  if (!row || typeof row !== "object") return row;
  const rk = Number(row.offer_rank);
  const rankOk = Number.isFinite(rk) && rk > 0;
  return {
    ...row,
    action_key: rankOk ? `select_offer:${rk}` : row.action_key,
    image: row.main_image ?? row.image,
  };
}

/**
 * Prefer full API cards when already loaded (same offer_rank / offer_id); otherwise use summary row.
 * @param {unknown[]} summaryRows
 * @param {unknown[]} messages
 */
export function mergeSummaryRowsWithLoadedCards(summaryRows, messages) {
  const loaded = collectAllLoadedAssistantCards(messages);
  /** @type {Map<number, unknown>} */
  const byRank = new Map();
  /** @type {Map<string, unknown>} */
  const byOfferId = new Map();
  for (const c of loaded) {
    const rk = Number(c?.offer_rank ?? c?.offerRank);
    if (Number.isFinite(rk) && rk > 0 && !byRank.has(rk)) byRank.set(rk, c);
    const oid = String(c?.offer_id ?? "").trim();
    if (oid && !byOfferId.has(oid)) byOfferId.set(oid, c);
  }
  return summaryRows.map((row) => {
    const rk = Number(row?.offer_rank);
    const oid = String(row?.offer_id ?? "").trim();
    const fromLoaded =
      (Number.isFinite(rk) && rk > 0 ? byRank.get(rk) : undefined) ||
      (oid ? byOfferId.get(oid) : undefined);
    if (fromLoaded) return fromLoaded;
    return summaryRowToChatCard(row);
  });
}

/**
 * When `last_results_summary` exists, hotel filter is driven by the full snapshot (not only the
 * current paginated `cards` batch). Returns `null` when there is no summary — caller keeps
 * per-message {@link filterCardsByHotelName} behavior (e.g. history without state).
 *
 * @param {unknown} state
 * @param {unknown[]} messages
 * @param {string|null} selectedName
 * @returns {unknown[]|null}
 */
export function buildConsolidatedHotelFilterCards(state, messages, selectedName) {
  if (!selectedName) return null;
  const rows = filterSummaryRowsForHotel(state, selectedName);
  if (!rows.length) return null;
  return mergeSummaryRowsWithLoadedCards(rows, messages);
}

/**
 * Cards to render for one assistant message, accounting for consolidated hotel filter.
 * @param {{
 *   message: unknown,
 *   messageIndex: number,
 *   selectedHotelFilter: string|null,
 *   hotelFilterAnchorMessageIndex: number,
 *   consolidatedCards: unknown[]|null,
 * }} args
 */
export function resolveAssistantMessageHotelCards({
  message,
  messageIndex,
  selectedHotelFilter,
  hotelFilterAnchorMessageIndex,
  consolidatedCards,
}) {
  if (!message || message.role !== "assistant" || !Array.isArray(message.cards)) return [];
  if (!selectedHotelFilter) return message.cards;
  if (consolidatedCards != null) {
    return messageIndex === hotelFilterAnchorMessageIndex ? consolidatedCards : [];
  }
  return filterCardsByHotelName(message.cards, selectedHotelFilter);
}

/**
 * @param {unknown} state
 */
export function countLastResultsSummaryRows(state) {
  const rows = state?.last_results_summary;
  return Array.isArray(rows) ? rows.length : 0;
}
